import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';

import {
  createMyInvoiceAutopayConsent,
  downloadMyInvoicePdf,
  reportMyInvoicePayment,
  retrieveMyInvoice,
  retrieveMyInvoicePaymentConfig,
  retrieveMyInvoicePaymentIntent,
  retrieveMyInvoicePaymentStatus,
} from '@src/api/invoices';
import ReportPaymentDialog from '@src/components/billing/ReportPaymentDialog';
import houseLogo from '@src/assets/logo-full.png';
import paths from '@src/paths';
import MetaChargesCard from '@src/components/billing/MetaChargesCard';

import {
  ACH_RECOMMENDED_TEXT,
  DEFAULT_FROM_LINES,
  PaymentForm,
  buildAddressLines,
  buildCompanyLines,
  dedupeLines,
  formatCurrency,
  formatDate,
  getPersistedPaymentFailureMessage,
  isMicrodepositVerificationMessage,
  normalizePaymentOptions,
  parseAmount,
  sanitizeLine,
} from '@src/pages/public/PublicInvoicePage';

export default function ClientInvoiceDetailPage() {
  const { number } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentError, setPaymentIntentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentSubmissionStatus, setPaymentSubmissionStatus] = useState('');
  const [paymentIntentRefreshNonce, setPaymentIntentRefreshNonce] = useState(0);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState('');
  const [stripeCurrency, setStripeCurrency] = useState('usd');
  const [stripePaymentMethodOptions, setStripePaymentMethodOptions] = useState({});
  const [microdepositVerificationUrl, setMicrodepositVerificationUrl] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [autopayConsentChecked, setAutopayConsentChecked] = useState(false);
  const [autopayConsentSubmitting, setAutopayConsentSubmitting] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentStatusRefreshing, setPaymentStatusRefreshing] = useState(false);
  const [achDialogOpen, setAchDialogOpen] = useState(false);
  const [autopayConsentDialogOpen, setAutopayConsentDialogOpen] = useState(false);
  // "I've already sent payment" — for money moving outside Stripe (direct
  // ACH, wire, check). Holds reminders and the day-6 pause off while it
  // settles; same statement as the emailed link's button.
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportedUntil, setReportedUntil] = useState(null);
  const paymentIntentRequestRef = useRef(0);

  const isPaid = useMemo(
    () => (invoice?.status ? String(invoice.status).toLowerCase() === 'paid' : false),
    [invoice],
  );
  const paymentComplete = isPaid || paymentSubmissionStatus === 'succeeded';
  const paymentReported = Boolean(reportedUntil || invoice?.payment_report_expires_on);
  // One report per invoice: a lapsed one means emailing billing instead.
  const reportUsed = Boolean(reportedUntil || invoice?.payment_reported);
  const autopayConsent = invoice?.autopay_consent || {};
  const autopayConsentText = invoice?.autopay_consent_text || 'Authorize future auto-charge.';
  const autopayConsentByMethod =
    autopayConsent && typeof autopayConsent.by_payment_method === 'object'
      ? autopayConsent.by_payment_method
      : {};
  const autopaySelectedMethodConsent = selectedPaymentMethod
    ? autopayConsentByMethod[selectedPaymentMethod] || null
    : null;
  const autopayAlreadyAuthorized = Boolean(autopaySelectedMethodConsent?.authorized);
  const persistedPaymentFailureMessage = useMemo(
    () => getPersistedPaymentFailureMessage(invoice),
    [invoice],
  );
  const hasMicrodepositVerificationState = useMemo(
    () =>
      [paymentError, paymentIntentError, persistedPaymentFailureMessage].some(
        isMicrodepositVerificationMessage,
      ),
    [paymentError, paymentIntentError, persistedPaymentFailureMessage],
  );

  // Fetch invoice
  useEffect(() => {
    if (!number) {
      setError('Missing invoice number.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    retrieveMyInvoice(number)
      .then((res) => setInvoice(res.data))
      .catch((err) => {
        console.error('Unable to load invoice', err);
        const detail = err?.response?.data?.detail;
        setError(detail || 'Unable to load this invoice.');
      })
      .finally(() => setLoading(false));
  }, [number]);

  // Fetch payment config
  useEffect(() => {
    if (!invoice || !number || isPaid || paymentComplete) {
      return;
    }
    paymentIntentRequestRef.current += 1;
    const requestId = paymentIntentRequestRef.current;
    setPaymentIntentLoading(true);
    setPaymentIntentError('');
    const timer = setTimeout(() => {
      retrieveMyInvoicePaymentConfig(number)
        .then((res) => {
          if (requestId !== paymentIntentRequestRef.current) return;
          const data = res.data || {};
          if (data.publishable_key) {
            setStripePromise((prev) => prev || loadStripe(data.publishable_key));
          }
          setCustomerSessionClientSecret(data.customer_session_client_secret || '');
          setStripeCurrency(data.currency || 'usd');
          setStripePaymentMethodOptions(data.payment_method_options || {});
        })
        .catch((err) => {
          console.error('Unable to initialize payment configuration', err);
          const detail = err?.response?.data?.detail;
          setPaymentIntentError(detail || 'Unable to initialize the payment form.');
        })
        .finally(() => setPaymentIntentLoading(false));
    }, 150);
    return () => clearTimeout(timer);
  }, [invoice?.number, number, isPaid, paymentComplete, paymentIntentRefreshNonce]);

  // Poll while processing
  useEffect(() => {
    if (!invoice || !number || isPaid || paymentSubmissionStatus !== 'processing') {
      return undefined;
    }
    const interval = setInterval(() => {
      retrieveMyInvoice(number)
        .then((res) => {
          const latest = res.data || null;
          setInvoice(latest);
          const hasFailure = latest && (String(latest.payment_failure_message || '').trim() || latest.payment_failed_at);
          if (hasFailure) {
            setPaymentSubmissionStatus('');
            setPaymentSuccess('');
          }
          if (latest?.status && String(latest.status).toLowerCase() === 'paid') {
            setPaymentSubmissionStatus('succeeded');
            setPaymentSuccess("Payment confirmed! We'll email you a receipt shortly.");
          }
        })
        .catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [invoice, isPaid, number, paymentSubmissionStatus]);

  const handleRefreshPaymentStatus = () => {
    if (!number) return;
    setPaymentStatusRefreshing(true);
    retrieveMyInvoicePaymentStatus(number)
      .then((res) => {
        const data = res.data || {};
        const latest = data.invoice || null;
        if (latest) {
          setInvoice(latest);
          if (String(latest.status).toLowerCase() === 'paid') {
            setPaymentSubmissionStatus('succeeded');
            setPaymentSuccess("Payment confirmed! We'll email you a receipt shortly.");
            setPaymentError('');
            setPaymentIntentError('');
            return;
          }
        }
        const intentStatus = String(data.payment_intent_status || '').trim().toLowerCase();
        if (intentStatus) {
          setPaymentSuccess(`Current payment status: ${intentStatus}.`);
          setPaymentSubmissionStatus(intentStatus === 'processing' ? 'processing' : '');
          setPaymentError('');
          setPaymentIntentError('');
        }
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setPaymentError(detail || 'Unable to refresh payment status.');
      })
      .finally(() => setPaymentStatusRefreshing(false));
  };

  const handleDownloadPdf = () => {
    if (!invoice) return;
    setDownloadLoading(true);
    setDownloadError('');
    downloadMyInvoicePdf(number)
      .then((blob) => {
        const fileName = `${invoice.number || number}.pdf`;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setDownloadError(detail || 'Unable to download the invoice PDF.');
      })
      .finally(() => setDownloadLoading(false));
  };

  const handleDownloadTerritoryCsv = () => {
    if (!invoice?.territory_breakdown?.length) return;
    const safeValue = (value) => {
      if (value === null || value === undefined) return '';
      const s = String(value);
      return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmtDate = (v) => {
      if (!v) return '';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    };
    const ps = fmtDate(invoice.period_start);
    const pe = fmtDate(invoice.period_end);
    const feeOnly = invoice.territory_breakdown_mode === 'fee_only';
    const rows = feeOnly
      ? [
          ['Territory', 'Vertical', 'Period start', 'Period end', 'Ad spend (paid directly by you)', 'Lead Gurus fee', 'Total'],
          ...invoice.territory_breakdown.map((r) => [
            safeValue(r.territory || ''), safeValue(r.vertical || ''), safeValue(ps), safeValue(pe), safeValue(r.base_spend ?? ''), safeValue(r.spend || '0.00'), safeValue(r.total_cost ?? r.spend ?? ''),
          ]),
        ]
      : [
          ['Territory', 'Vertical', 'Period start', 'Period end', 'Spend'],
          ...invoice.territory_breakdown.map((r) => [
            safeValue(r.territory || ''), safeValue(r.vertical || 'Unassigned'), safeValue(ps), safeValue(pe), safeValue(r.spend || '0.00'),
          ]),
        ];
    if (invoice.territory_breakdown_total) {
      rows.push([
        'Total', '', safeValue(ps), safeValue(pe),
        ...(feeOnly ? [safeValue(invoice.territory_ad_spend_total || '')] : []),
        safeValue(invoice.territory_breakdown_total),
        ...(feeOnly ? [safeValue(invoice.territory_total_cost || '')] : []),
      ]);
    }
    const csv = rows.map((r) => r.map(safeValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${invoice.number || number}-territory-vertical-breakdown.csv`;
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  const handleDownloadClientBreakdownCsv = () => {
    if (!clientBreakdown.length || !invoice) return;
    const safeValue = (value) => {
      if (value === null || value === undefined) return '';
      const s = String(value);
      return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmtDate = (v) => {
      if (!v) return '';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    };
    const ps = fmtDate(invoice.period_start);
    const pe = fmtDate(invoice.period_end);
    const rows = [
      ['Client', 'Period start', 'Period end', 'Spend', 'Fee', 'Line total'],
      ...clientBreakdown.map((r) => [
        safeValue(r.client_name || ''), safeValue(ps), safeValue(pe),
        safeValue(r.spend_amount || '0.00'), safeValue(r.fee_amount || '0.00'), safeValue(r.line_total || '0.00'),
      ]),
    ];
    rows.push(['Total', safeValue(ps), safeValue(pe), '', '', safeValue(invoice.total_amount || '0.00')]);
    const csv = rows.map((r) => r.map(safeValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${invoice.number || number}-client-breakdown.csv`;
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  const achLines = useMemo(() => {
    if (!invoice?.ach_instructions) return [];
    return invoice.ach_instructions.split('\n').filter((line) => line.trim());
  }, [invoice]);

  const pageTitle = invoice ? `Invoice ${invoice.number}` : 'Invoice';

  const fromLines = useMemo(
    () => buildCompanyLines(invoice?.billing_from_snapshot, DEFAULT_FROM_LINES),
    [invoice],
  );

  const paymentOptions = useMemo(() => normalizePaymentOptions(invoice), [invoice]);

  useEffect(() => {
    if (selectedPaymentMethod) return;
    if (paymentOptions.length) {
      setSelectedPaymentMethod(paymentOptions[0].type);
      return;
    }
    setSelectedPaymentMethod('card');
  }, [paymentOptions, selectedPaymentMethod]);

  useEffect(() => {
    if (!isPaid) return;
    setPaymentIntentError('');
    setPaymentError('');
    setPaymentSuccess("Payment received. We'll email you a receipt shortly.");
    setCustomerSessionClientSecret('');
    setMicrodepositVerificationUrl('');
  }, [isPaid]);

  const createPaymentIntentForSelection = async (paymentMethod) => {
    if (!number) return null;
    const existingMethodConsent =
      (invoice?.autopay_consent?.by_payment_method || {})[paymentMethod] || null;
    const response = await retrieveMyInvoicePaymentIntent(number, {
      payment_method: paymentMethod,
      save_payment_method: Boolean(existingMethodConsent?.authorized) || autopayConsentChecked,
    });
    const data = response?.data || {};
    return {
      clientSecret: data.client_secret || '',
      paymentIntentId: data.payment_intent_id || '',
    };
  };

  const submitAutopayConsent = async (paymentMethod) => {
    return createMyInvoiceAutopayConsent(number, { payment_method: paymentMethod });
  };

  const invoiceTotalAmount = useMemo(
    () => parseAmount(invoice?.balance_due ?? invoice?.total_amount),
    [invoice],
  );
  const clientBreakdown = useMemo(() => {
    if (!invoice) return [];
    if (Array.isArray(invoice.client_breakdown)) return invoice.client_breakdown;
    if (Array.isArray(invoice.client_breakdown_snapshot)) return invoice.client_breakdown_snapshot;
    return [];
  }, [invoice]);
  const selectedOption = useMemo(
    () => paymentOptions.find((o) => o.type === selectedPaymentMethod) || paymentOptions[0],
    [paymentOptions, selectedPaymentMethod],
  );

  const billToLines = useMemo(() => {
    if (!invoice) return [];
    const lines = [];
    const clientName = sanitizeLine(invoice.client_name);
    if (clientName) lines.push(clientName);
    if (Array.isArray(invoice.billing_contacts_snapshot)) {
      invoice.billing_contacts_snapshot.forEach((contact) => {
        if (!contact || typeof contact !== 'object') return;
        const name = sanitizeLine(contact.name);
        const email = sanitizeLine(contact.email || contact.address);
        const parts = [name, email].filter(Boolean);
        if (parts.length) lines.push(parts.join(' \u2022 '));
      });
    }
    buildAddressLines(invoice.billing_address_snapshot).forEach((line) => lines.push(line));
    return dedupeLines(lines);
  }, [invoice]);

  const attachments = invoice?.attachments ?? [];

  const resetPaymentState = () => {
    setPaymentIntentError('');
    setPaymentError('');
    setPaymentSuccess('');
    setPaymentSubmissionStatus('');
    setCustomerSessionClientSecret('');
    setStripePaymentMethodOptions({});
    setMicrodepositVerificationUrl('');
    setAutopayConsentSubmitting(false);
    setPaymentIntentRefreshNonce((prev) => prev + 1);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Helmet>
        <title>{pageTitle} &middot; Lead Gurus</title>
      </Helmet>
      <Stack spacing={4}>
        <Box>
          <Button
            size="small"
            onClick={() => navigate(paths.myInvoices.list)}
            sx={{ textTransform: 'none', mb: 1 }}
          >
            &larr; Back to invoices
          </Button>
        </Box>

        {invoice?.urgency === 'critical' && (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Your account is at risk of being paused
            </Typography>
            <Typography variant="body2">
              This invoice is {invoice.days_past_due}+ days past due.
              Lead delivery will be paused until payment is received. Please pay immediately.
            </Typography>
          </Alert>
        )}
        {invoice?.urgency === 'final' && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Final notice &mdash; account pause imminent
            </Typography>
            <Typography variant="body2">
              This invoice is {invoice.days_past_due} days past due.
              Your account will be paused tomorrow if payment is not received.
            </Typography>
          </Alert>
        )}
        {invoice?.urgency === 'warning' && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Invoice past due
            </Typography>
            <Typography variant="body2">
              This invoice is {invoice.days_past_due} days past due.
              Accounts are paused at 6 days overdue. Please submit payment to keep your lead flow running.
            </Typography>
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
        ) : invoice ? (
          <Stack spacing={4}>
            {/* Invoice header card */}
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <CardHeader
                sx={{ pb: 0 }}
                title={
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box component="img" src={houseLogo} alt="Lead Gurus logo" sx={{ height: 40, width: 'auto' }} />
                        {invoice.client_logo_file ? (
                          <Box
                            component="img"
                            src={invoice.client_logo_file}
                            alt={`${invoice.client_name || 'Client'} logo`}
                            sx={{ height: 40, width: 'auto', maxWidth: { xs: '100%', sm: 160 }, objectFit: 'contain' }}
                          />
                        ) : null}
                      </Stack>
                      <Box textAlign={{ xs: 'left', sm: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Invoice {invoice.number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {invoice.period_label}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleDownloadPdf}
                          disabled={downloadLoading}
                          sx={{ borderRadius: 9999, textTransform: 'none', mt: 1 }}
                        >
                          {downloadLoading ? 'Downloading\u2026' : 'Download PDF'}
                        </Button>
                      </Box>
                    </Stack>
                  </Stack>
                }
              />
              <CardContent>
                <Stack spacing={4}>
                  {downloadError ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{downloadError}</Alert>
                  ) : null}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">From</Typography>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {fromLines.map((line, i) => (
                          <Typography key={`from-${i}`} variant={i === 0 ? 'body1' : 'body2'} color={i === 0 ? 'text.primary' : 'text.secondary'}>
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Bill to</Typography>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {billToLines.map((line, i) => (
                          <Typography key={`bill-${i}`} variant={i === 0 ? 'body1' : 'body2'} color={i === 0 ? 'text.primary' : 'text.secondary'}>
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">Client</Typography>
                      <Typography variant="h6">{invoice.client_name}</Typography>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3 }}>Amount due</Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                        {formatCurrency(invoice.balance_due ?? invoice.total_amount)}
                      </Typography>
                      {parseFloat(invoice.amount_paid) > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(invoice.amount_paid)} paid of {formatCurrency(invoice.total_amount)} total
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Issue date</Typography>
                          <Typography>{formatDate(invoice.issue_date)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Due date</Typography>
                          <Typography>{formatDate(invoice.due_date)}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
                {invoice.external_notes ? (
                  <Alert severity="info" sx={{ mt: 4, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Client message</Typography>
                    <Typography whiteSpace="pre-line">{invoice.external_notes}</Typography>
                  </Alert>
                ) : null}
              </CardContent>
            </Card>

            {/* Payment card */}
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <CardHeader title="Pay securely online" sx={{ pb: 0 }} />
              <CardContent>
                <Stack spacing={3}>
                  {paymentIntentError && !paymentComplete ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{paymentIntentError}</Alert>
                  ) : null}
                  {paymentSubmissionStatus === 'processing' && !isPaid && !paymentError && !paymentIntentError && !persistedPaymentFailureMessage ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {paymentSuccess || 'Payment submitted. Stripe is processing your payment.'}
                    </Alert>
                  ) : null}
                  {paymentComplete ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      {paymentSuccess || "Payment received. We'll email you a receipt shortly."}
                    </Alert>
                  ) : null}
                  {hasMicrodepositVerificationState && !paymentComplete ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Additional bank verification is required before ACH payment can complete. Stripe may send two small microdeposits in 1-2 business days.
                      Check your bank activity, complete Stripe verification, then click <strong>Refresh payment status</strong>.
                    </Alert>
                  ) : null}
                  {hasMicrodepositVerificationState && microdepositVerificationUrl && !paymentComplete ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="contained" size="small" onClick={() => window.open(microdepositVerificationUrl, '_blank', 'noopener,noreferrer')}>
                        Verify bank now
                      </Button>
                      <Button variant="outlined" size="small" disabled={paymentStatusRefreshing} onClick={handleRefreshPaymentStatus}>
                        {paymentStatusRefreshing ? 'Refreshing\u2026' : 'Refresh payment status'}
                      </Button>
                    </Stack>
                  ) : null}
                  {hasMicrodepositVerificationState && !microdepositVerificationUrl && !paymentComplete ? (
                    <Box display="flex" justifyContent="flex-end">
                      <Button variant="outlined" size="small" disabled={paymentStatusRefreshing} onClick={handleRefreshPaymentStatus}>
                        {paymentStatusRefreshing ? 'Refreshing\u2026' : 'Refresh payment status'}
                      </Button>
                    </Box>
                  ) : null}
                  {paymentError && !paymentComplete && !hasMicrodepositVerificationState ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{paymentError}</Alert>
                  ) : null}
                  {persistedPaymentFailureMessage && !paymentComplete && !paymentError && !hasMicrodepositVerificationState && !paymentSubmitting ? (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{persistedPaymentFailureMessage}</Alert>
                  ) : null}
                  {selectedPaymentMethod === 'us_bank_account' && !paymentComplete && !paymentError && !paymentIntentError && !persistedPaymentFailureMessage && paymentSubmissionStatus !== 'processing' ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {ACH_RECOMMENDED_TEXT} Manual bank entry may require microdeposit verification (1-2 business days). Bank login is usually faster.
                    </Alert>
                  ) : null}
                  {(paymentIntentError || paymentError || persistedPaymentFailureMessage) && !paymentComplete && !hasMicrodepositVerificationState && paymentSubmissionStatus !== 'processing' ? (
                    <Box display="flex" justifyContent="flex-end">
                      <Button variant="outlined" size="small" onClick={resetPaymentState}>Try again</Button>
                    </Box>
                  ) : null}
                  {paymentIntentLoading && !paymentComplete ? (
                    <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : null}
                  {stripePromise && !paymentComplete && paymentSubmissionStatus !== 'processing' ? (
                    <PaymentForm
                      customerSessionClientSecret={customerSessionClientSecret}
                      stripePromise={stripePromise}
                      currency={stripeCurrency}
                      paymentMethodOptions={stripePaymentMethodOptions}
                      number={number}
                      token={null}
                      invoiceTotal={invoiceTotalAmount}
                      invoiceTotalRaw={invoice?.balance_due ?? invoice?.total_amount}
                      options={paymentOptions}
                      selectedOption={selectedOption}
                      onSelectionChange={(type) => setSelectedPaymentMethod(type)}
                      selectionDisabled={paymentIntentLoading}
                      onSuccess={(message, status) => {
                        if (!message) { setPaymentSubmissionStatus(''); setPaymentSuccess(''); return; }
                        setPaymentSubmissionStatus(status === 'processing' ? 'processing' : 'succeeded');
                        setPaymentSuccess(message);
                        setPaymentIntentError('');
                        setPaymentError('');
                      }}
                      onError={(message) => {
                        setPaymentSubmissionStatus('');
                        setPaymentError(message);
                        if (message) setPaymentSuccess('');
                      }}
                      onMicrodepositVerificationUrl={(url) => setMicrodepositVerificationUrl(url || '')}
                      createPaymentIntent={createPaymentIntentForSelection}
                      submitAutopayConsent={submitAutopayConsent}
                      autopayConsentChecked={autopayConsentChecked}
                      autopayConsentText={autopayConsentText}
                      autopayConsentSubmitting={autopayConsentSubmitting}
                      autopayAlreadyAuthorized={autopayAlreadyAuthorized}
                      onAutopayConsentChange={(value) => {
                        if (value === 'open_details') { setAutopayConsentDialogOpen(true); return; }
                        setAutopayConsentChecked(Boolean(value));
                      }}
                      onAutopayConsentSaved={(consent) => {
                        setAutopayConsentSubmitting(false);
                        if (!consent) return;
                        setInvoice((prev) => prev ? { ...prev, autopay_consent: consent } : prev);
                      }}
                      setAutopayConsentSubmitting={setAutopayConsentSubmitting}
                      submitting={paymentSubmitting}
                      setSubmitting={setPaymentSubmitting}
                    />
                  ) : null}
                  {!stripePromise && !paymentIntentLoading && !paymentIntentError && !paymentComplete && paymentSubmissionStatus !== 'processing' ? (
                    <Stack spacing={1.5} alignItems="flex-start">
                      <Typography color="text.secondary">
                        Payment form unavailable. Please reach out to billing@leadgurus.com for assistance.
                      </Typography>
                      <Button variant="outlined" size="small" onClick={resetPaymentState}>Retry payment form</Button>
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {paymentReported && !paymentComplete ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Thanks — we&apos;ll look out for it
                </Typography>
                <Typography variant="body2">
                  We&apos;ve paused payment reminders for this invoice while your
                  payment reaches us. Nothing else is needed from you.
                </Typography>
              </Alert>
            ) : null}

            {!paymentComplete && !reportUsed ? (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => setReportDialogOpen(true)}
                sx={{ borderRadius: 9999, textTransform: 'none', fontWeight: 600, py: 1.25 }}
              >
                Already sent this payment? Let us know
              </Button>
            ) : null}

            <Box display="flex" justifyContent="center">
              <Button variant="outlined" size="small" onClick={() => setAchDialogOpen(true)} sx={{ borderRadius: 9999, textTransform: 'none' }}>
                Manual ACH details
              </Button>
            </Box>

            <ReportPaymentDialog
              open={reportDialogOpen}
              invoiceNumber={invoice?.number}
              submit={(form) => reportMyInvoicePayment(number, form)}
              onClose={() => setReportDialogOpen(false)}
              onReported={(until) => {
                setReportedUntil(until);
                setReportDialogOpen(false);
              }}
            />

            {/* ACH Dialog */}
            <Dialog open={achDialogOpen} onClose={() => setAchDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Manual ACH details</DialogTitle>
              <DialogContent dividers>
                {achLines.length ? (
                  <Stack spacing={1.5}>
                    {achLines.map((line) => (
                      <Typography key={line} fontFamily="'Roboto Mono', monospace" fontSize={13}>{line}</Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">ACH instructions will be provided upon request.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAchDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Autopay consent dialog */}
            <Dialog open={autopayConsentDialogOpen} onClose={() => setAutopayConsentDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Future Auto-Charge Authorization</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Typography variant="body2">{autopayConsentText}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    By opting in, you authorize Lead Gurus to keep this payment method on file and use it for future invoices until you revoke that authorization in writing.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAutopayConsentDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Line items */}
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardHeader title="Invoice items" sx={{ pb: 0 }} />
              <CardContent>
                {invoice.line_items?.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.line_items.map((item) => (
                        <TableRow key={`${item.description}-${item.line_total}`}>
                          <TableCell>
                            <Typography fontWeight={600}>{item.display_description || item.description}</Typography>
                            {item.fee_percentage ? (
                              <Typography variant="caption" color="text.secondary">Fee {item.fee_percentage}%</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      {parseFloat(invoice.amount_paid) > 0 && (
                        <>
                          <TableRow>
                            <TableCell>Invoice total</TableCell>
                            <TableCell align="right">{formatCurrency(invoice.total_amount)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Paid</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>&minus;{formatCurrency(invoice.amount_paid)}</TableCell>
                          </TableRow>
                        </>
                      )}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {parseFloat(invoice.amount_paid) > 0 ? 'Remaining balance' : 'Total due'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(invoice.balance_due ?? invoice.total_amount)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary">No line items available.</Typography>
                )}
              </CardContent>
            </Card>

            {/* Territory breakdown */}
            {invoice.territory_breakdown?.length ? (
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardHeader
                  title="Territory & Vertical Breakdown"
                  sx={{ pb: 0 }}
                  action={
                    <Button size="small" variant="outlined" onClick={handleDownloadTerritoryCsv} sx={{ borderRadius: 9999, textTransform: 'none' }}>
                      Download CSV
                    </Button>
                  }
                />
                <CardContent>
                  {invoice.territory_breakdown_mode === 'fee_only' ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Your ad spend is billed to you directly by the ad platforms and is shown here for reference only. This invoice is the Lead Gurus fee on that spend.
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Territory</TableCell>
                            <TableCell>Vertical</TableCell>
                            <TableCell align="right">Ad spend (paid directly by you)</TableCell>
                            <TableCell align="right">
                              Lead Gurus fee{invoice.fee_percentage ? ` (${Number(invoice.fee_percentage)}%)` : ''}
                            </TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoice.territory_breakdown.map((row) => (
                            <TableRow key={`${row.territory}-${row.territory_id || 'na'}`}>
                              <TableCell>{row.territory}</TableCell>
                              <TableCell>{row.vertical || ''}</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {row.base_spend != null ? formatCurrency(row.base_spend) : ''}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(row.spend)}</TableCell>
                              <TableCell align="right">{formatCurrency(row.total_cost ?? row.spend)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }} colSpan={2}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>{formatCurrency(invoice.territory_ad_spend_total)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(invoice.territory_breakdown_total)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(invoice.territory_total_cost)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Territory</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell align="right">Spend</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.territory_breakdown.map((row) => (
                          <TableRow key={`${row.territory}-${row.territory_id || 'na'}`}>
                            <TableCell>{row.territory}</TableCell>
                            <TableCell>{row.vertical || 'Unassigned'}</TableCell>
                            <TableCell align="right">{formatCurrency(row.spend)}</TableCell>
                          </TableRow>
                        ))}
                        {invoice.territory_breakdown_total ? (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }} colSpan={2}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(invoice.territory_breakdown_total)}</TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            ) : null}

            {/* Client breakdown */}
            {clientBreakdown.length ? (
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardHeader
                  title="Client Breakdown"
                  sx={{ pb: 0 }}
                  action={
                    <Button size="small" variant="outlined" onClick={handleDownloadClientBreakdownCsv} sx={{ borderRadius: 9999, textTransform: 'none' }}>
                      Download CSV
                    </Button>
                  }
                />
                <CardContent>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Fee</TableCell>
                        <TableCell align="right">Line total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientBreakdown.map((row, i) => (
                        <TableRow key={`${row.client_id || i}-${row.client_name || 'client'}`}>
                          <TableCell>{row.client_name || '\u2014'}</TableCell>
                          <TableCell align="right">{formatCurrency(row.spend_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.fee_amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.line_total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }} colSpan={3}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(invoice.total_amount)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : null}

            <MetaChargesCard metaCharges={invoice.meta_charges} invoiceNumber={invoice.number || number} />

            {/* Attachments */}
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardHeader title="Attachments" sx={{ pb: 0 }} />
              <CardContent>
                {attachments.length ? (
                  <Stack spacing={2}>
                    {attachments.map((att) => {
                      const key = att.id || att.download_url || att.filename;
                      const uploadLabel = att.uploaded_at ? `Uploaded ${formatDate(att.uploaded_at)}` : null;
                      const parts = [att.notes, uploadLabel].filter(Boolean);
                      return (
                        <Box key={key}>
                          <Typography
                            component="a"
                            href={att.download_url || att.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: 600, display: 'inline-block' }}
                          >
                            {att.filename || att.original_filename || 'Attachment'}
                          </Typography>
                          {parts.length ? (
                            <Typography variant="body2" color="text.secondary">{parts.join(' \u2022 ')}</Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No attachments included.</Typography>
                )}
              </CardContent>
            </Card>

            <Divider />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Need help? Email <a href="mailto:billing@leadgurus.com">billing@leadgurus.com</a> and include your invoice number {invoice.number}.
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Container>
  );
}
